'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { SimplifiedView } from '@/app/components';
import { ControlDefinition } from '@/types/api';
import { isTauri } from '@/lib/runtime-bridge';
import { desktopService } from '@/lib/desktop-service';

interface Sketch {
  id: string;
  title: string;
  description?: string;
  code: string;
  svgThumbnail?: string;
  controls?: ControlDefinition[];
  author: {
    id: string;
    name?: string;
    username?: string;
  };
}

export default function SketchViewPage() {
  const params = useParams();
  const sketchId = params.sketchId as string;

  const [sketch, setSketch] = useState<Sketch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSketch();
  }, [sketchId]);

  const fetchSketch = async () => {
    try {
      if (isTauri()) {
        const localSketch = await desktopService.loadSketch(sketchId);
        if (localSketch) {
          setSketch({
            ...localSketch,
            author: { id: 'local', username: 'You' }
          });
        } else {
          setError('Sketch not found locally');
        }
        return;
      }

      const response = await fetch(`/api/sketches/${sketchId}`);
      const data = await response.json();

      if (data.success) {
        setSketch(data.sketch);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load sketch');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#1e1e1e',
        color: '#ccc',
      }}>
        Loading...
      </div>
    );
  }

  if (error || !sketch) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#1e1e1e',
        color: '#f44336',
      }}>
        Error: {error || 'Sketch not found'}
      </div>
    );
  }

  return (
    <SimplifiedView
      sketchId={sketch.id}
      sketchTitle={sketch.title}
      sketchDescription={sketch.description}
      authorUsername={sketch.author.username}
      authorName={sketch.author.name}
      code={sketch.code}
      controls={sketch.controls || []}
      svgThumbnail={sketch.svgThumbnail}
    />
  );
}
